/**
 * ProctoringEventBus — a tiny typed pub/sub used to fan a single set of SDK
 * callbacks out to any number of hook consumers.
 *
 * Registering ONE `ProctoringCallbacks` set on the SDK client and re-emitting
 * here is what lets multiple `useProctoring()` consumers subscribe/unsubscribe
 * independently without ever creating duplicate SDK listeners.
 */

import type { ProctoringEventHandler, ProctoringEventMap, ProctoringEventName } from './types'

type AnyHandler = (payload: unknown) => void

export class ProctoringEventBus {
    private readonly listeners = new Map<ProctoringEventName, Set<AnyHandler>>()

    on<E extends ProctoringEventName>(event: E, handler: ProctoringEventHandler<E>): () => void {
        let set = this.listeners.get(event)
        if (!set) {
            set = new Set()
            this.listeners.set(event, set)
        }
        const anyHandler = handler as AnyHandler
        set.add(anyHandler)
        return () => {
            set.delete(anyHandler)
        }
    }

    off<E extends ProctoringEventName>(event: E, handler: ProctoringEventHandler<E>): void {
        this.listeners.get(event)?.delete(handler as AnyHandler)
    }

    once<E extends ProctoringEventName>(event: E, handler: ProctoringEventHandler<E>): () => void {
        const wrapper = ((payload: ProctoringEventMap[E]) => {
            this.off(event, wrapper)
            handler(payload)
        }) as ProctoringEventHandler<E>
        return this.on(event, wrapper)
    }

    emit<E extends ProctoringEventName>(event: E, payload: ProctoringEventMap[E]): void {
        const set = this.listeners.get(event)
        if (!set) return
        for (const handler of set) {
            try {
                handler(payload)
            } catch (err) {
                console.error('[proctoring] event handler threw', err)
            }
        }
    }

    clear(): void {
        this.listeners.clear()
    }
}
